import { useMemo, useState } from 'react';
import { Copy, Check, ShieldCheck } from 'lucide-react';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/Button';
import { Select } from '@/shared/ui/Select';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/toast';
import { ApiException } from '@/shared/api/client';
import { formatEur } from '@/lib/utils';
import { useSendApproval, useApprovalPrereqs } from './reports.api';
import { useProject } from '@/features/projects/projects.api';
import type { Report } from '@/shared/api/types';

export function SendApprovalDialog({
  open,
  onClose,
  report,
  onSent,
}: {
  open: boolean;
  onClose: () => void;
  report: Report;
  onSent: () => void;
}) {
  const { data: project } = useProject(open ? report.project.id : null);
  const { contacts, extras } = useApprovalPrereqs(
    open ? report.project.id : null,
    project?.customer.id ?? null,
  );
  const send = useSendApproval();
  const push = useToast((s) => s.push);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [contactId, setContactId] = useState('');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Nur Positionen, die noch keinem Kunden vorgelegt wurden.
  const sendable = useMemo(
    () => (extras.data?.items ?? []).filter((e) => (e._count?.approvalItems ?? 0) === 0),
    [extras.data],
  );
  const authorizedContacts = (contacts.data?.contacts ?? []).filter((c) => c.approvalAuthorized);

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const total = sendable
    .filter((e) => selected.has(e.id))
    .reduce((sum, e) => sum + Number(e.netAmount ?? 0), 0);

  const onSend = async () => {
    if (selected.size === 0) {
      push('Bitte mindestens eine Zusatzleistung auswählen.', 'error');
      return;
    }
    try {
      const res = await send.mutateAsync({
        reportId: report.id,
        extraServiceIds: [...selected],
        ...(contactId ? { contactId } : {}),
      });
      setResultUrl(res.url);
      push('Freigabelink erzeugt.');
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'Senden fehlgeschlagen.', 'error');
    }
  };

  const onCopy = async () => {
    if (!resultUrl) return;
    try {
      await navigator.clipboard.writeText(resultUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      push('Kopieren nicht möglich. Bitte manuell markieren.', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={resultUrl ? onSent : onClose} title="An Kunde senden">
      {resultUrl ? (
        <div className="space-y-3">
          <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Dieser Link wird nur <strong>einmal</strong> angezeigt. Jetzt kopieren und dem Kunden
            senden (E-Mail o. Ä.).
          </div>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded bg-slate-100 px-2 py-1.5 text-xs">
              {resultUrl}
            </code>
            <Button size="md" variant="secondary" onClick={onCopy}>
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Kopiert' : 'Kopieren'}
            </Button>
          </div>
          <Button className="w-full" onClick={onSent}>
            Fertig
          </Button>
        </div>
      ) : contacts.isLoading || extras.isLoading ? (
        <Spinner />
      ) : (
        <div className="space-y-4">
          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">
              Zusatzleistungen zur Freigabe
            </h3>
            {sendable.length === 0 ? (
              <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500">
                Keine sendefähigen Positionen. Zusatzleistungen müssen intern bestätigt und bepreist
                sein (Bereich „Zusatzleistungen").
              </p>
            ) : (
              <ul className="space-y-2">
                {sendable.map((e) => (
                  <li key={e.id}>
                    <label className="flex cursor-pointer items-center justify-between rounded-lg border border-slate-200 px-3 py-2 has-[:checked]:border-brand-500 has-[:checked]:bg-brand-50">
                      <span className="flex items-center gap-2 text-sm text-slate-900">
                        <input
                          type="checkbox"
                          checked={selected.has(e.id)}
                          onChange={() => toggle(e.id)}
                          className="h-4 w-4 rounded"
                        />
                        {e.title}
                      </span>
                      <span className="text-sm font-medium">{formatEur(e.netAmount)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            {selected.size > 0 && (
              <p className="mt-2 text-right text-sm font-semibold text-slate-900">
                Summe: {formatEur(total)}
              </p>
            )}
          </div>

          <Select
            label="Ansprechpartner (freigabeberechtigt)"
            value={contactId}
            onChange={(e) => setContactId(e.target.value)}
          >
            <option value="">Ohne konkreten Ansprechpartner</option>
            {authorizedContacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          {authorizedContacts.length === 0 && (
            <p className="text-xs text-amber-700">
              Kein freigabeberechtigter Ansprechpartner hinterlegt (Bereich „Kunden").
            </p>
          )}

          <Button
            className="w-full"
            onClick={onSend}
            loading={send.isPending}
            disabled={sendable.length === 0}
          >
            <ShieldCheck className="h-4 w-4" /> Freigabelink erzeugen
          </Button>
        </div>
      )}
    </Dialog>
  );
}
